/**
 * AssistedTravelDemo - Passenger Decision Governance Demo
 * 
 * Demonstrates governed decision-making for vulnerable passengers
 * facing service disruptions, with bounded disclosure and attributable
 * evidence throughout.
 */

import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { 
  AlertCircle,
  Train,
  Accessibility,
  Beaker,
  Loader2
} from 'lucide-react';
import ScenarioLab from './ScenarioLab';
import { 
  ValuePropositionBanner, 
  OutcomeCards 
} from './AssistedTravelDemo/ui';
import { 
  RecommendationCard, 
  UnsafeAdviceCard, 
  PassengerContextCards,
  DisruptionsCard,
  AdvisoriesCard, 
  CryptoReceiptCard,
  PassengerCommunication 
} from './AssistedTravelDemo/results';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssistedTravelDemo = () => {
  const [activeTab, setActiveTab] = useState('demo');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const runDemo = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/passenger/disruption/demo`);
      if (!response.ok) throw new Error('Demo failed');
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - API_URL is a module constant
  
  const decision = result?.governed_decision;
  const excludedAdvisory = decision?.advisories_received?.find(
    a => decision?.advisories_excluded?.some(e => e.advisory_id === a.advisory_id)
  );
  
  return (
    <div data-testid="assisted-travel-demo" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Accessibility className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl font-bold text-white">Assisted Travel Advisor</h1>
          </div>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Protecting vulnerable passengers with governed decisions backed by verified evidence
          </p>
        </div>
        
        {/* Tab Switcher */}
        <div className="flex justify-center mb-6">
          <div className="bg-slate-800/50 rounded-xl p-1 border border-slate-700 inline-flex">
            <button
              data-testid="tab-demo"
              onClick={() => { setActiveTab('demo'); setResult(null); }}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'demo'
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Train className="w-4 h-4" />
              Headline Demo
            </button>
            <button
              data-testid="tab-lab"
              onClick={() => { setActiveTab('lab'); setResult(null); }}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'lab'
                  ? 'bg-purple-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Beaker className="w-4 h-4" />
              Scenario Lab
            </button>
          </div>
        </div>
        
        {/* Scenario Lab Mode */}
        {activeTab === 'lab' && (
          <div>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
              <h2 className="font-semibold text-white flex items-center gap-2 mb-2">
                <Beaker className="w-5 h-5 text-purple-400" />
                Partner Test Mode
              </h2>
              <p className="text-slate-400 text-sm">
                Configure bounded test scenarios with structured parameters. No free-text input - 
                all scenarios are repeatable and produce governed outcomes with attributable receipts.
              </p>
            </div>
            <ScenarioLab />
          </div>
        )}
        
        {/* Headline Demo Mode */}
        {activeTab === 'demo' && (
          <>
            {/* Value Proposition Banner */}
            {!result && <ValuePropositionBanner />}
            
            {/* Run Demo Button */}
            {!result && (
              <div className="text-center mb-8">
                <Button 
                  data-testid="run-demo-btn"
                  onClick={runDemo}
                  disabled={loading}
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 px-10 py-6 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Running Scenario...
                    </>
                  ) : (
                    <>
                      <Train className="w-5 h-5 mr-2" />
                      Run Headline Demo
                    </>
                  )}
                </Button>
                <p className="text-slate-500 text-sm mt-3">
                  Simulates a reduced-mobility passenger facing service disruptions
                </p>
              </div>
            )}
            
            {/* Error Display */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{error}</span>
              </div>
            )}
            
            {/* Results */}
            {decision && (
              <div className="space-y-6">
                {/* Section 1: The Recommendation */}
                <RecommendationCard decision={decision} />
                
                {/* Section 2: Excluded Unsafe Advice */}
                <UnsafeAdviceCard excludedAdvisory={excludedAdvisory} />
                
                {/* Section 3: Business Outcomes */}
                <OutcomeCards decision={decision} />
                
                {/* Section 4: Passenger & Journey Context */}
                <PassengerContextCards decision={decision} />
                
                {/* Active Disruptions */}
                <DisruptionsCard disruptions={decision.active_disruptions} />
                
                {/* Section 5: Verified Advisories */}
                <AdvisoriesCard decision={decision} />
                
                {/* Section 6: Cryptographic Receipt */}
                <CryptoReceiptCard decision={decision} />
                
                {/* Passenger Communication */}
                <PassengerCommunication summary={decision.passenger_summary} />
                
                {/* Reset */}
                <div className="text-center pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => setResult(null)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Run Again
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssistedTravelDemo;
